import type { CancelablePromise } from '@/backend/market/core/CancelablePromise'
import { OpenAPI } from '@/backend/market/core/OpenAPI'
import { request as __request } from '@/backend/market/core/request'
import type { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type {
  DriverShiftAdherenceEntity,
  DriverShiftAdherenceDetailEntity
} from '../../domain/entities/DriverShiftAdherenceEntity'
import type {
  DriverAcceptanceRateEntity,
  DriverAcceptanceRateDetailEntity
} from '../../domain/entities/DriverAcceptanceRateEntity'

/**
 * Admin endpoints for the driver ranking daily-metrics engine (shift adherence
 * and acceptance rate). Uses the same core request/auth layer as the generated
 * `@/backend/market` services (identical bearer token, base URL, error handling).
 *
 * Why this is hand-written instead of the generated
 * `DriverShiftAdherenceService` / `DriverAcceptanceRateService`:
 * the generated services pass `fromDate` / `toDate` through the request core's
 * `options.query`, which runs every date-looking value through `recurse()` and
 * rewrites `YYYY-MM-DD` into a full `moment.utc(v).toISOString()` timestamp
 * (`2026-07-22T00:00:00.000Z`). The backend for these endpoints expects a plain
 * operational **date** string (`YYYY-MM-DD`) with no time. To send exactly that,
 * the list endpoints bake the query into the URL string (which `getUrl` does NOT
 * run through `recurse`) instead of using `options.query`. The detail endpoints
 * already carry the date as a path param, so they are unaffected either way.
 *
 * The operational day runs 06:00 → 06:00 (UTC+3); `fromDate` / `toDate` are
 * operational dates. The server clamps `limit` to a max of 10; `page` is
 * 0-indexed.
 */
export class DriverMetricsService {
  private static buildDateRangeQuery(params: {
    fromDate: string
    toDate: string
    page?: number
    limit?: number
  }): string {
    const query = new URLSearchParams()
    // Sent verbatim as strings — no Date/ISO conversion, so the API receives
    // exactly `YYYY-MM-DD`.
    query.set('fromDate', params.fromDate)
    query.set('toDate', params.toDate)
    if (params.page !== undefined) query.set('page', String(params.page))
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    return query.toString()
  }

  /**
   * **Required Policy:** `ViewDriverShiftAdherence`
   * @throws ApiError
   */
  public static getDriverShiftAdherenceAdmin({
    driverId,
    fromDate,
    toDate,
    page,
    limit
  }: {
    driverId: number
    fromDate: string
    toDate: string
    page?: number
    limit?: number
  }): CancelablePromise<PaginateEntity<DriverShiftAdherenceEntity[]>> {
    const queryString = DriverMetricsService.buildDateRangeQuery({ fromDate, toDate, page, limit })
    return __request(OpenAPI, {
      method: 'GET',
      url: `/api/admin/v1/drivers/{driverId}/shift-adherence?${queryString}`,
      path: {
        driverId
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`
      }
    })
  }

  /**
   * **Required Policy:** `ViewDriverShiftAdherence`
   * @throws ApiError
   */
  public static getDriverShiftAdherenceDetailAdmin({
    driverId,
    driverShiftId
  }: {
    driverId: number
    driverShiftId: string
  }): CancelablePromise<DriverShiftAdherenceDetailEntity> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/admin/v1/drivers/{driverId}/shift-adherence/{driverShiftId}',
      path: {
        driverId,
        driverShiftId
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`
      }
    })
  }

  /**
   * **Required Policy:** `ViewDriverAcceptanceRate`
   * @throws ApiError
   */
  public static getDriverAcceptanceRateAdmin({
    driverId,
    fromDate,
    toDate,
    page,
    limit
  }: {
    driverId: number
    fromDate: string
    toDate: string
    page?: number
    limit?: number
  }): CancelablePromise<PaginateEntity<DriverAcceptanceRateEntity[]>> {
    const queryString = DriverMetricsService.buildDateRangeQuery({ fromDate, toDate, page, limit })
    return __request(OpenAPI, {
      method: 'GET',
      url: `/api/admin/v1/drivers/{driverId}/acceptance-rate?${queryString}`,
      path: {
        driverId
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`
      }
    })
  }

  /**
   * **Required Policy:** `ViewDriverAcceptanceRate`
   * @throws ApiError
   */
  public static getDriverAcceptanceRateDetailAdmin({
    driverId,
    date
  }: {
    driverId: number
    /** Operational date (YYYY-MM-DD). */
    date: string
  }): CancelablePromise<DriverAcceptanceRateDetailEntity> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/admin/v1/drivers/{driverId}/acceptance-rate/{date}',
      path: {
        driverId,
        date
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`
      }
    })
  }
}
